import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { HttpService } from '@ats/api-sdk';
import { EfpAppConfigService } from '@ats/app-config';
import { ConfigService, EfpDataStorageService, FlagsService } from '@ats/service-base';
import { EfactoryPubSubService } from '@ats/service-pubsub';
import { EfactoryUserInfoService, EfactoryUserService, ILoggedInUser } from '@ats/service-user';
import { AuthService, User } from '@auth0/auth0-angular';
import { TranslocoService } from '@ngneat/transloco';
import { productFruits } from 'product-fruits';
import { of, Subject } from 'rxjs';
import { catchError, filter, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  userId!: string;
  userSideId!: string;
  userPerson!: ILoggedInUser;

  mastheadFlagsmithEnvId!: string;
  buildNumber: string = '';

  /**
   * Container being viewed
   */
  currentRoute!: string;

  featureToggles$ = this.flagsService.featureFlagsForPage('light_dark_mode_switch');

  authError$ = this.auth.error$.pipe(
    filter((error) => !!error),
    tap(() => {
      // If error redirect to unguarded component to prevent login loop
      this.router.navigateByUrl('auth_error');
    })
  );

  user$ = this.auth.user$.pipe(
    filter((user) => user !== null && user !== undefined),
    // Set global variables
    tap((user) => {
      this.userId = user?.email as string;
      this.userPerson = {
        personId: (user as User)?.['https://efactorypro.com/id'] as string,
        cosmosId: (user as User)?.['https://efactorypro.com/uuid'] as string,
        tenantId: 1,
        firstName: '',
        lastName: '',
        fullName: '',
        email: (user as User)?.email ?? '',
        sites: [],
        roles: (user as User)?.['https://efactorypro.com/roles'],
        locale: '',
        defaultSiteId: '',
        currentSiteId: '',
        currentSite: null,
        preferences: []
      } as unknown as ILoggedInUser;
    }),
    // Initialize NewRelic
    tap((user) => {
      // Set custom NewRelic attributes for session traces
      // biome-ignore lint/suspicious/noExplicitAny: Allowing as newrelic is in the global space
      (window as any)['newrelic']?.setCustomAttribute('user', user?.email as string);
    }),
    // Initialize Flagsmith
    tap((user) => {
      this.initBulletTrainFeatureToggles(user?.['https://efactorypro.com/id'] as string);
    }),
    // Apply theme
    tap((user) => {
      const userId = user?.email?.toLowerCase();
      // Apply dark/light mode for user
      const theme = localStorage.getItem(`efp-theme-${userId}`) === 'dark' ? 'dark' : 'light';
      localStorage.setItem(`efp-theme-${userId}`, theme);
      document.documentElement.classList.add(`${theme}Mode`);

      this.initProductFruits(user as User);
    }),
    // Get logged in user
    switchMap(() => this.userService.getUserInfo(this.userPerson.cosmosId)),
    // Set the user in userInfo service
    map((user) => {
      this.userPerson = this.userInfoService.set(user);

      // Announce user is ready
      this.ct.$pub('user:ready', { person: this.userInfoService.getUser() });

      return this.userPerson;
    }),
    catchError(() => {
      this.router.navigate(['/auth_error']);

      // return of({});
      return of(null);
    }),
    startWith({
      personId: '',
      cosmosId: '',
      tenantId: 1,
      firstName: '',
      lastName: '',
      fullName: '',
      email: '',
      sites: [],
      roles: ['User'],
      locale: '',
      defaultSiteId: '',
      currentSiteId: '',
      currentSite: null,
      preferences: []
    })
  );

  timezone$ = this.auth.user$.pipe(
    filter((u) => !!u),
    map(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'),
    tap((tz) => this.dataService.set('timezone', tz))
  );

  private ngUnsubscribe = new Subject<void>();

  constructor(
    public auth: AuthService,
    private router: Router,
    private translate: TranslocoService,
    private appconfig: EfpAppConfigService,
    public userInfoService: EfactoryUserInfoService,
    private userService: EfactoryUserService,
    private httpService: HttpService,
    private ct: EfactoryPubSubService,
    private flagsService: FlagsService,
    private configService: ConfigService,
    private dataService: EfpDataStorageService
  ) {
    this.httpService.config = {
      apiHost: appconfig.config.uris.aks
    };
  }

  ngOnInit(): void {
    this.mastheadFlagsmithEnvId = this.appconfig.config['mastheadFlagsmithEnvId'] as string;
    this.configService.setUris(this.appconfig.config.uris);

    this.initTranslationService();

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        map((e: NavigationEnd) => {
          const firstPathSegment = e.url.split('/')[1]?.split('?')[0];

          if (firstPathSegment) {
            this.currentRoute = firstPathSegment;
          }
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe();

    this.buildNumber =
      (this.appconfig.config['buildNumber'] as string)?.match(/^(\d*\.\d*\.\d*)|local/)?.[0] ?? '';
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initProductFruits(user: User): void {
    productFruits.init(
      'Fv0sEjXxNSXcmo1A',
      navigator.languages[0].split('-')?.[0].toLowerCase() ?? 'en',
      { username: user.email ?? '' }
    );
  }

  initBulletTrainFeatureToggles(personId: string): void {
    this.flagsService.initializeFlagsmith(
      this.appconfig.config['bulletTrainEnvId'] as string,
      personId
    );
  }

  initTranslationService(): void {
    let lang: string = navigator.language;

    if (lang.toLowerCase().substring(0, 2) === 'en') {
      lang = 'en-US';
    } else if (lang.toLowerCase().substring(0, 2) === 'es') {
      lang = 'es-MX';
    }

    this.translate.setDefaultLang(lang);
  }
}
